import React, { useState, useEffect } from 'react';
import './App.css';
import ProductCard from './components/ProductCard';
import ProductViewer from './components/ProductViewer';
import ConfigurationPanel from './components/ConfigurationPanel';
import SpatialConfig from './components/SpatialConfig';
import ConfigDetails from './components/ConfigDetails';
// We're no longer using the EmailForm component
// import EmailForm from './components/EmailForm';
import { 
  isDoubleDrumAvailable, 
  isLayoutAvailable, 
  isArrangementAvailable,
  getElectricOptions,
  getNonElectricOptions,
  getAccessOptions
} from './components/productRules';

function App() {
  // State pentru toată configurația - modificat pentru a include power options, hookType, și quantity
  const [config, setConfig] = useState({
    selectedModel: 'tip1', 
    powerType: 'electric',
    electricOption: 'independent',
    accessOption: 'with-it-platform',
    fractions: {
      residual: {
        enabled: true,
        drumType: 'single',
        quantity: 1,
        volume: '3000' // Added fixed volume for residual
      },
      biodegradable: {
        enabled: true,
        drumType: 'single',
        quantity: 1,
        volume: '3000' // Added fixed volume for biodegradable
      },
      plastic: {
        enabled: true,
        drumType: 'single',
        quantity: 1,
        volume: '3000' // Added selectable volume for plastic
      },
      paper: {
        enabled: true,
        drumType: 'single',
        quantity: 1,
        volume: '3000' // Volume property for paper
      },
      glass: {
        enabled: true,
        drumType: 'single',
        quantity: 1,
        volume: '3000' // Added fixed volume for glass
      },
    },
    layout: 'linear',
    accessories: {
      colantare: false // Doar opțiunea de colantare cu valoarea implicită false
    },
    hookType: 'simplu',
    spatialArrangement: 'grouped',
    platformFeatures: [
      {
        id: 'status-monitoring',
        label: 'Urmărire stare de funcționare',
        description: 'Permite monitorizarea stării de funcționare în timp real a eco-insulei, inclusiv raportarea automată a erorilor sau defecțiunilor.',
        enabled: true
      },
      {
        id: 'geolocation',
        label: 'Geolocație',
        description: 'Oferă informații despre locația exactă a eco-insulei, facilitând găsirea acesteia de către utilizatori și operatorii de salubritate.',
        enabled: true
      },
      {
        id: 'fill-level',
        label: 'Volum de umplere',
        description: 'Monitorizează continuu nivelul de umplere pentru fiecare fracție, optimizând rutele de colectare și prevenind supraîncărcarea.',
        enabled: true
      },
      {
        id: 'user-identification',
        label: 'Identificare utilizatori',
        description: 'Identifică utilizatorii și monitorizează frecvența aruncării și tipurile de fracții utilizate, permițând facturarea individuală și analiza comportamentului.',
        enabled: true
      },
      {
        id: 'electric-lock',
        label: 'Yală electrică cu card de acces',
        description: 'Sistem de blocare/deblocare electronică cu cititor de carduri RFID pentru control acces autorizat la eco-insulă.',
        enabled: true
      }
    ]
   , judet: '' // nou câmp pentru județ
  });

  // State pentru a controla vizibilitatea formularului
  const [showContactForm, setShowContactForm] = useState(false);
  
  // State pentru a gestiona starea formularului
  const [formSubmitStatus, setFormSubmitStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    success: false,
    message: ''
  });

  // Produse disponibile cu descrieri detaliate
  const products = [
    {
      id: 'tip1',
      name: 'Eco-insula Tip 1',
      description: 'Supraterana - 1100L',
      imageUrl: 'eco-tip1.png',
      features: [
        'Cel mai comun tip de eco-insulă, utilizat frecvent în zonele urbane',
        'Operare simplă și accesibilă pentru majoritatea operatorilor de salubritate',
        'Design compact, ideal pentru amplasare în spații mici și alei înguste',
        'Poate fi amplasat în locuri greu accesibile pentru autospecialele de colectare'
      ]
    },
    {
      id: 'tip2',
      name: 'Eco-insula Tip 2',
      description: 'Supraterana - 3000-5000L',
      imageUrl: 'eco-tip2.png',
      features: [
                <>
          Dimensiuni containere:
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>3000L: 1200 × 1780 × 1720 mm (fără cârlig de prindere)</li>
            <li>5000L: 1870 x 1780 x 1720 mm (fără cârlig de prindere)</li>
          </ul>
        </>,
        'Nu necesită pregătire specială a amplasamentului, fiind necesară doar suprafața fizică',
        'Capacitate de colectare mărită, între 3-5 mc între descărcări',
        'Construcție robustă, rezistentă la intemperii și utilizare intensivă',
        'Ideal pentru zone cu densitate mare de populație'
      ]
    },
    {
      id: 'tip3',
      name: 'Eco-insula Tip 3',
      description: 'Subterana - 3000-5000L',
      imageUrl: 'eco-tip3.png',
      features: [
        'Estetică superioară, cu amprentă vizuală minimă deasupra solului',
        'Poate fi amplasată în fața sau în lateralul imobilului fără impact vizual negativ',
        'Eliminarea mirosurilor neplăcute datorită depozitării subterane',
        'Protecție împotriva accesului neautorizat și a vandalismului'
      ]
    }
  ];

  // Effect pentru validarea configurației când se schimbă modelul
  useEffect(() => {
    // Validarea tipurilor de tambur
    const updatedFractions = { ...config.fractions };
    let fractionsChanged = false;

    // Verifică fiecare fracție și resetează tamburul dublu dacă nu este disponibil
    Object.keys(updatedFractions).forEach(fractionName => {
      if (updatedFractions[fractionName].drumType === 'double' && 
          !isDoubleDrumAvailable(config.selectedModel, fractionName)) {
        updatedFractions[fractionName].drumType = 'single';
        fractionsChanged = true;
      }
      
      // For tip2, ensure all fractions have volume properties
      if (config.selectedModel === 'tip2') {
        // For plastic and paper, allow user selection but initialize if missing
        if ((fractionName === 'plastic' || fractionName === 'paper') && !updatedFractions[fractionName].volume) {
          updatedFractions[fractionName].volume = '3000';
          fractionsChanged = true;
        }
        // For residual, biodegradable, and glass, ensure fixed 3000L volume
        else if ((fractionName === 'residual' || fractionName === 'biodegradable' || fractionName === 'glass')) {
          if (updatedFractions[fractionName].volume !== '3000') {
            updatedFractions[fractionName].volume = '3000';
            fractionsChanged = true;
          }
        }
      }
      // For other models, remove volume properties if they exist
      else if (updatedFractions[fractionName].volume) {
        delete updatedFractions[fractionName].volume;
        fractionsChanged = true;
      }
    });

    // Reset layout to 'linear' when changing from tip1 to tip2 or tip3
    let updatedLayout = config.layout;
    if (config.selectedModel !== 'tip1') {
      updatedLayout = 'linear';
    }

    // Validarea aranjamentului spațial
    let updatedArrangement = config.spatialArrangement;
    let arrangementChanged = false;

    if (!isArrangementAvailable(config.selectedModel, config.spatialArrangement)) {
      // Find the first available arrangement for this model
      const arrangements = ['individual', 'grouped', 'custom'];
      updatedArrangement = arrangements.find(arr => 
        isArrangementAvailable(config.selectedModel, arr)
      ) || 'individual';
      arrangementChanged = true;
    }

    // Validarea opțiunilor de alimentare
    let updatedPowerOptions = {
      powerType: config.powerType,
      electricOption: config.electricOption,
      accessOption: config.accessOption
    };
    let powerOptionsChanged = false;

    // Verifică dacă opțiunea electrică este disponibilă
    if (config.powerType === 'electric') {
      const electricOptions = getElectricOptions(config.selectedModel);
      if (!electricOptions.some(opt => opt.id === config.electricOption)) {
        // Setează prima opțiune electrică disponibilă
        if (electricOptions.length > 0) {
          updatedPowerOptions.electricOption = electricOptions[0].id;
          
          // Resetează și opțiunea de acces
          const accessOptions = getAccessOptions(config.selectedModel, electricOptions[0].id);
          if (accessOptions.length > 0) {
            updatedPowerOptions.accessOption = accessOptions[0].id;
          }
        }
        powerOptionsChanged = true;
      } else {
        // Verifică dacă opțiunea de acces este disponibilă
        const accessOptions = getAccessOptions(config.selectedModel, config.electricOption);
        if (!accessOptions.some(opt => opt.id === config.accessOption)) {
          if (accessOptions.length > 0) {
            updatedPowerOptions.accessOption = accessOptions[0].id;
          }
          powerOptionsChanged = true;
        }
      }
    } else {
      // Verifică dacă opțiunea non-electrică este disponibilă
      const nonElectricOptions = getNonElectricOptions(config.selectedModel);
      if (nonElectricOptions.length === 0) {
        // Dacă nu există opțiuni non-electrice, setează la electric
        updatedPowerOptions.powerType = 'electric';
        const electricOptions = getElectricOptions(config.selectedModel);
        if (electricOptions.length > 0) {
          updatedPowerOptions.electricOption = electricOptions[0].id;
          const accessOptions = getAccessOptions(config.selectedModel, electricOptions[0].id);
          if (accessOptions.length > 0) {
            updatedPowerOptions.accessOption = accessOptions[0].id;
          }
        }
        powerOptionsChanged = true;
      }
    }

    // Validate hook type - only relevant for tip2 and tip3
    let updatedHookType = config.hookType;
    let hookTypeChanged = false;
    
    if (config.selectedModel === 'tip1' && config.hookType) {
      // Reset hook type when switching to tip1
      updatedHookType = undefined;
      hookTypeChanged = true;
    } else if ((config.selectedModel === 'tip2' || config.selectedModel === 'tip3') && !config.hookType) {
      // Set default hook type when switching to tip2 or tip3
      updatedHookType = 'simplu';
      hookTypeChanged = true;
    }

    // Aplicăm actualizările
    setConfig(prev => ({
      ...prev,
      fractions: fractionsChanged ? updatedFractions : prev.fractions,
      layout: updatedLayout,
      spatialArrangement: arrangementChanged ? updatedArrangement : prev.spatialArrangement,
      powerType: powerOptionsChanged ? updatedPowerOptions.powerType : prev.powerType,
      electricOption: powerOptionsChanged ? updatedPowerOptions.electricOption : prev.electricOption,
      accessOption: powerOptionsChanged ? updatedPowerOptions.accessOption : prev.accessOption,
      // Add the hook type update
      hookType: hookTypeChanged ? updatedHookType : prev.hookType,
      // Păstrăm accesoriile așa cum sunt (doar colantare)
      accessories: prev.accessories
    }));
  }, [config.selectedModel]);

  // Prevent form submission and setup form event listener
  useEffect(() => {
    if (showContactForm) {
      const setupFormListener = () => {
        const form = document.querySelector('.wpcf7-form');
        if (form) {
          // Remove existing event listeners to prevent duplicates
          const newForm = form.cloneNode(true);
          form.parentNode.replaceChild(newForm, form);
          
          // Add event listener to the submit button
          const submitBtn = newForm.querySelector('input[type="submit"]');
          if (submitBtn) {
            submitBtn.addEventListener('click', handleFormSubmit);
          }
          
          // Listen for wpcf7 events if they're available
          document.addEventListener('wpcf7submit', handleCF7Submit);
          document.addEventListener('wpcf7invalid', handleCF7Invalid);
          document.addEventListener('wpcf7mailsent', handleCF7Success);
          document.addEventListener('wpcf7mailfailed', handleCF7Error);
        } else {
          // If form is not yet available, try again shortly
          setTimeout(setupFormListener, 300);
        }
      };
      
      setupFormListener();
      
      // Cleanup event listeners on unmount
      return () => {
        document.removeEventListener('wpcf7submit', handleCF7Submit);
        document.removeEventListener('wpcf7invalid', handleCF7Invalid);
        document.removeEventListener('wpcf7mailsent', handleCF7Success);
        document.removeEventListener('wpcf7mailfailed', handleCF7Error);
      };
    }
  }, [showContactForm]);

  // Handler for manual form submission
  const handleFormSubmit = (e) => {
    // Don't prevent default here - let CF7 handle the submission
    // but update our state to show "submitting"
    setFormSubmitStatus({
      isSubmitting: true,
      isSubmitted: false,
      success: false,
      message: 'Se trimite cererea...'
    });
  };
  
  // Handler for CF7 submission event
  const handleCF7Submit = (e) => {
    console.log('Form submitted', e.detail);
  };
  
  // Handler for CF7 invalid fields
  const handleCF7Invalid = (e) => {
    setFormSubmitStatus({
      isSubmitting: false,
      isSubmitted: true,
      success: false,
      message: 'Vă rugăm să completați toate câmpurile obligatorii.'
    });
  };
  
  // Handler for CF7 success
  const handleCF7Success = (e) => {
    setFormSubmitStatus({
      isSubmitting: false,
      isSubmitted: true,
      success: true,
      message: 'Cererea dumneavoastră a fost trimisă cu succes! Vă vom contacta în curând.'
    });
    
    // Optionally reset form after success (but keep it visible)
    const form = document.querySelector('.wpcf7-form');
    if (form) {
      // Reset only the input fields, not the configuration textarea
      const inputs = form.querySelectorAll('input:not([name="your-configuration"])');
      inputs.forEach(input => {
        if (input.type !== 'submit' && input.type !== 'hidden') {
          input.value = '';
        }
      });
    }
  };
  
  // Handler for CF7 error
  const handleCF7Error = (e) => {
    setFormSubmitStatus({
      isSubmitting: false,
      isSubmitted: true,
      success: false,
      message: 'A apărut o eroare la trimiterea cererii. Vă rugăm să încercați din nou.'
    });
  };

  // Funcție pentru actualizarea modelului selectat
  const selectModel = (modelId) => {
    setConfig({
      ...config,
      selectedModel: modelId
    });
  };

  // Funcție actualizată pentru actualizarea fracțiilor
  const updateFractions = (fractionName, property, value) => {
    setConfig({
      ...config,
      fractions: {
        ...config.fractions,
        [fractionName]: {
          ...config.fractions[fractionName],
          [property]: value
        }
      }
    });
  };

  // Funcție nouă pentru actualizarea tipului de tambur per fracție
  const updateFractionDrumType = (fractionName, drumType) => {
    // Verifică dacă tamburul dublu este disponibil pentru această fracție și acest model
    if (drumType === 'double' && !isDoubleDrumAvailable(config.selectedModel, fractionName)) {
      // Ignoră schimbarea dacă nu este disponibilă
      return;
    }

    setConfig({
      ...config,
      fractions: {
        ...config.fractions,
        [fractionName]: {
          ...config.fractions[fractionName],
          drumType: drumType
        }
      }
    });
  };

  // Funcție pentru actualizarea cantității per fracție
  const updateFractionQuantity = (fractionName, quantity) => {
    // Asigurăm că avem un număr întreg pozitiv
    const newQuantity = Math.max(1, parseInt(quantity) || 1);
    
    setConfig({
      ...config,
      fractions: {
        ...config.fractions,
        [fractionName]: {
          ...config.fractions[fractionName],
          quantity: newQuantity
        }
      }
    });
  };

  // Funcție pentru actualizarea volumului per fracție (pentru tip2, doar pentru plastic și paper)
  const updateFractionVolume = (fractionName, newVolume) => {
    // Only allow volume changes for plastic and paper fractions in tip2
    if (config.selectedModel !== 'tip2' || (fractionName !== 'plastic' && fractionName !== 'paper')) return;
    
    setConfig(prevConfig => {
      return {
        ...prevConfig,
        fractions: {
          ...prevConfig.fractions,
          [fractionName]: {
            ...prevConfig.fractions[fractionName],
            volume: newVolume
          }
        }
      };
    });
    
    // If we need to update an image based on volume change, we could trigger a render update
    console.log(`Volume changed for ${fractionName} to ${newVolume}L - image should update`);
  };

  // Funcție pentru actualizarea platform features
  const updatePlatformFeature = (featureId, enabled) => {
    setConfig({
      ...config,
      platformFeatures: config.platformFeatures.map(feature => 
        feature.id === featureId ? { ...feature, enabled } : feature
      )
    });
  };

  // Funcție pentru actualizarea layout-ului - disponibil doar pentru tip1
  const updateLayout = (newLayout) => {
    // Verifică dacă layout-ul este disponibil pentru modelul selectat (numai tip1)
    if (config.selectedModel !== 'tip1') {
      return;
    }

    setConfig({
      ...config,
      layout: newLayout
    });
  };

  // Funcție pentru actualizarea accesoriilor - simplificată pentru doar colantare
  const updateAccessories = (accessoryName, isSelected) => {
    // Singura opțiune este colantare, nu mai avem restricții
    setConfig({
      ...config,
      accessories: {
        ...config.accessories,
        [accessoryName]: isSelected
      }
    });
  };

  // Funcție pentru actualizarea aranjamentului spațial
  const updateSpatialArrangement = (arrangement) => {
    // Verifică dacă aranjamentul este disponibil pentru modelul selectat
    if (!isArrangementAvailable(config.selectedModel, arrangement)) {
      return;
    }

    setConfig({
      ...config,
      spatialArrangement: arrangement
    });
  };

  // Funcție pentru actualizarea tipului de cârlig - doar pentru tip2 și tip3
  const updateHookType = (hookType) => {
    // Verifică dacă modelul selectat este tip2 sau tip3
    if (config.selectedModel === 'tip2' || config.selectedModel === 'tip3') {
      setConfig({
        ...config,
        hookType
      });
    }
  };

  // Funcție pentru actualizarea detaliilor suplimentare
  const updateAdditionalDetails = (details) => {
    setConfig({
      ...config,
      additionalDetails: details
    });
  };

    // 🔹 actualizează județul din config (folosit de SelectJudet)
  const updateJudet = (judet) => {
    setConfig(prev => ({
      ...prev,
      judet
    }));
  };


  // Funcție pentru actualizarea opțiunilor de alimentare
  const updatePowerOptions = (powerOptions) => {
    // Avoid unnecessary updates by comparing with previous values
    if (
      powerOptions.powerType === config.powerType &&
      powerOptions.electricOption === config.electricOption &&
      powerOptions.accessOption === config.accessOption &&
      JSON.stringify(powerOptions.platformFeatures) === JSON.stringify(config.platformFeatures)
    ) {
      return; // No changes, skip update
    }
    
    // Actualizăm starea cu noile opțiuni de alimentare
    setConfig(prevConfig => {
      return {
        ...prevConfig,
        powerType: powerOptions.powerType,
        electricOption: powerOptions.electricOption,
        accessOption: powerOptions.accessOption,
        platformFeatures: powerOptions.platformFeatures || prevConfig.platformFeatures
      };
    });
  };

  // Format configuration details for the Contact Form 7
  const formatConfigDetails = () => {
    // Get model name
    let modelName = 'Necunoscut';
    if (config.selectedModel === 'tip1') modelName = 'Eco-Insulă Tip 1';
    if (config.selectedModel === 'tip2') modelName = 'Eco-Insulă Tip 2';
    if (config.selectedModel === 'tip3') modelName = 'Eco-Insulă Tip 3';
    
    // Get fractions with quantities and volume for tip2
    const fractions = Object.keys(config.fractions || {})
      .filter(key => config.fractions[key].enabled)
      .map(key => {
        let name;
        switch(key) {
          case 'residual': name = 'Rezidual'; break;
          case 'glass': name = 'Sticlă'; break;
          case 'plastic': name = 'Plastic'; break;
          case 'paper': name = 'Hârtie'; break;
          case 'biodegradable': name = 'Biodegradabil'; break;
          default: name = key;
        }
        
        // Add quantity to the display
        const quantity = config.fractions[key].quantity || 1;
        let fractionText = `${name} (${quantity} buc)`;
        
        // Add volume for all fractions in tip2
        if (config.selectedModel === 'tip2') {
          if (key === 'plastic' || key === 'paper') {
            // Selectable volume for plastic and paper
            fractionText += ` - ${config.fractions[key].volume || '3000'}L`;
          } else {
            // Fixed 3000L volume for residual, biodegradable, and glass
            fractionText += ` - 3000L`;
          }
        }
        
        return fractionText;
      })
      .join(', ');
    
     // Generate full configuration text
  let details = `Județ: ${config.judet || 'Nespecificat'}\n\n`;
  details += `Model: ${modelName}\n\n`;
  
  details += `Fracții: ${fractions}\n\n`;
    
    // Add drum type details
    const drumDetails = Object.keys(config.fractions || {})
      .filter(key => config.fractions[key].enabled)
      .map(key => {
        let name;
        switch(key) {
          case 'residual': name = 'Rezidual'; break;
          case 'glass': name = 'Sticlă'; break;
          case 'plastic': name = 'Plastic'; break;
          case 'paper': name = 'Hârtie'; break;
          case 'biodegradable': name = 'Biodegradabil'; break;
          default: name = key;
        }
        
        const drumType = config.fractions[key].drumType === 'single' ? 'Tambur simplu' : 'Tambur dublu';
        return `${name}: ${drumType}`;
      })
      .join(', ');
    
    details += `Tipuri tambur: ${drumDetails}\n\n`;
    
    // Add volume details for tip2
    if (config.selectedModel === 'tip2') {
      const volumeDetails = Object.keys(config.fractions || {})
        .filter(key => config.fractions[key].enabled)
        .map(key => {
          let name;
          switch(key) {
            case 'residual': name = 'Rezidual'; break;
            case 'glass': name = 'Sticlă'; break;
            case 'plastic': name = 'Plastic'; break;
            case 'paper': name = 'Hârtie'; break;
            case 'biodegradable': name = 'Biodegradabil'; break;
            default: name = key;
          }
          
          if (key === 'plastic' || key === 'paper') {
            return `${name}: ${config.fractions[key].volume || '3000'}L`;
          } else {
            return `${name}: 3000L`;
          }
        })
        .join(', ');
      
      details += `Volume: ${volumeDetails}\n\n`;
    }
    
    // Add power options
    if (config.powerType === 'electric') {
      details += `Alimentare: Varianta electrică - `;
      details += `${config.electricOption === 'independent' ? 'Independentă (autonomă)' : 'Legată la sursă de curent'}`;
      details += ` - ${config.accessOption === 'with-it-platform' ? 'Cu acces la platformă IT' : 'Fără acces la platformă IT (cu cartelă)'}\n\n`;
      
      // Add platform features if selected
      if (config.accessOption === 'with-it-platform' && config.platformFeatures) {
        const enabledFeatures = config.platformFeatures
          .filter(feature => feature.enabled)
          .map(feature => feature.label);
        
        if (enabledFeatures.length > 0) {
          details += `Funcționalități platformă: ${enabledFeatures.join(', ')}\n\n`;
        }
      }
    } else {
      details += `Alimentare: Varianta fără curent - Cu deschidere mecanică\n\n`;
    }
    
    // Add layout details for tip1
    if (config.selectedModel === 'tip1') {
      let layoutText = 'Liniară';
      if (config.layout === 'L-shape') layoutText = 'Tip L';
      if (config.layout === 'U-shape') layoutText = 'Tip U';
      if (config.layout === 'back-to-back') layoutText = 'Spate în spate';
      details += `Așezare: ${layoutText}\n\n`;
    }
    
    // Add hook type details for tip2 and tip3
    if ((config.selectedModel === 'tip2' || config.selectedModel === 'tip3') && config.hookType) {
      let hookTypeText = '';
      switch(config.hookType) {
        case 'simplu': hookTypeText = 'Cârlig Simplu'; break;
        case 'dublu-linie': hookTypeText = 'Dublu cârlig în linie'; break;
        case 'dublu-paralel': hookTypeText = 'Dublu cârlig în paralel'; break;
        case 'ciuperca': hookTypeText = 'Cârlig tip Ciupercă'; break;
        default: hookTypeText = 'Necunoscut';
      }
      details += `Tip cârlig: ${hookTypeText}\n\n`;
    }
    
    // Add spatial arrangement
    let arrangementText = 'Necunoscut';
    if (config.spatialArrangement === 'individual') arrangementText = 'Eco-insule individuale';
    if (config.spatialArrangement === 'grouped') arrangementText = 'Eco-insule grupate';
    if (config.spatialArrangement === 'custom') arrangementText = 'Configurare personalizată';
    details += `Aranjament spațial: ${arrangementText}\n\n`;
    
    // Add accessory - doar colantare
    const accessories = [];
    if (config.accessories?.colantare) accessories.push('Colantare');
    details += `Accesorii: ${accessories.length > 0 ? accessories.join(', ') : 'Nici unul'}\n\n`;
    
    // Add additional details if provided
    if (config.additionalDetails) {
      details += `Detalii suplimentare: ${config.additionalDetails}\n`;
    }
    
    return details;
  };

  // Toggle display of the contact form in-line
const handleShowContactForm = () => {
  // ✅ 1. Validare județ înainte să afișăm formularul
  if (!config.judet || config.judet.trim() === '') {
    alert('Vă rugăm să selectați județul înainte de a solicita oferta.');
    return;
  }

  // ✅ 2. Dacă județul există, afișăm formularul
  setShowContactForm(true);

  // Resetăm statusul formularului
  setFormSubmitStatus({
    isSubmitting: false,
    isSubmitted: false,
    success: false,
    message: ''
  });

  // Așteptăm să se render-uiască formularul CF7, apoi completăm textarea
  setTimeout(() => {
    const configField = document.querySelector('textarea[name="your-configuration"]');
    if (configField) {
      configField.value = formatConfigDetails();
    } else {
      console.error('Configuration field not found. Check your Contact Form 7 field name.');
    }
  }, 100);
};


  // Hide the contact form
  const handleCancelForm = () => {
    setShowContactForm(false);
    
    // Reset form status when hiding the form
    setFormSubmitStatus({
      isSubmitting: false,
      isSubmitted: false,
      success: false,
      message: ''
    });
  };

  return (
    <div className="app-container" style={{ zIndex: 1, position: 'relative' }}>
      <header className="app-header">
        <h4>Configurator Eco-Insule</h4>
      </header>
      
      <div className="main-content">
        {/* Panoul din stânga cu produsele */}
        <div className="products-panel">
          {products.map(product => (
            <ProductCard 
              key={product.id}
              product={product}
              isActive={config.selectedModel === product.id}
              onSelect={() => selectModel(product.id)}
            />
          ))}
        </div>
        
        {/* Panoul central cu vizualizarea produsului */}
        <div className="visualization-panel">
          {!showContactForm ? (
            <>
              <ProductViewer 
                selectedModel={config.selectedModel}
                config={config}
              />
              
              <SpatialConfig 
                arrangement={config.spatialArrangement}
                onArrangementChange={updateSpatialArrangement}
                selectedProduct={config.selectedModel}
              />
              
              <ConfigDetails config={config} />
              
              <div className="action-buttons">
                <button 
                  className="btn btn-primary request-quote-btn"
                  onClick={handleShowContactForm}
                >
                  Solicită ofertă
                </button>
              </div>
            </>
          ) : (
            <div className="contact-form-container">
              <h3 className="contact-form-title">Solicită o ofertă personalizată</h3>
              
              {/* Status message for form submission */}
              {formSubmitStatus.isSubmitted && (
                <div className={`form-status-message ${formSubmitStatus.success ? 'success' : 'error'}`}>
                  {formSubmitStatus.message}
                </div>
              )}
              
              {/* Loading indicator during submission */}
              {formSubmitStatus.isSubmitting && (
                <div className="form-status-message">
                  Se trimite cererea... <span className="form-submitting-indicator"></span>
                </div>
              )}
              
              {/* Display the Contact Form 7 directly in this container */}
              <div 
                id="inline-contact-form"
                className={formSubmitStatus.isSubmitting ? 'submitting-form' : ''}
                dangerouslySetInnerHTML={{ 
                  __html: window.contactFormHtml || '<div class="loading-form">Se încarcă formularul...</div>' 
                }}
              />
              
              <div className="form-navigation">
                <button 
                  className="btn btn-secondary cancel-btn"
                  onClick={handleCancelForm}
                  disabled={formSubmitStatus.isSubmitting}
                >
                  Înapoi la configurator
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Panoul din dreapta cu opțiunile de configurare */}
        <div className="options-panel">
          <ConfigurationPanel 
            config={config}
            selectedProduct={config.selectedModel}
            onFractionChange={updateFractions}
            onLayoutChange={updateLayout}
            onAccessoryChange={updateAccessories}
            onDetailsChange={updateAdditionalDetails}
            onFractionDrumChange={updateFractionDrumType}
            onPowerOptionsChange={updatePowerOptions}
            onHookTypeChange={updateHookType}
            onFractionQuantityChange={updateFractionQuantity}
            onFractionVolumeChange={updateFractionVolume}
            onPlatformFeatureChange={updatePlatformFeature}
            onJudetChange={updateJudet}
          />
        </div>
      </div>
    </div>
  );
}

export default App;